/**
 * Tubulars write layer — assemblies / casing / pipe-tally POST + PUT against
 * Oxbo-P, injected onto the shared `pApi` so the read-through cache, tag
 * invalidation and auth headers are reused (the shared contract file
 * `src/api/pApi.ts` stays untouched — this keeps branches merging cleanly).
 *
 * Every write invalidates the event's Tubulars list tag so the panel refetches
 * on success. PUT keys on `entity_id` (not `id`), per the Oxbo-P contract.
 * Request/response types are declared locally here (not in the shared
 * `src/api/types.ts`).
 */
import { pApi } from "@api/pApi";
import type { AssemblyOut, CasingOut, PipeTallyOut } from "@api/types";

// ── Local write types (mirror the Oxbo-P contract §Tubulars) ────────────────

/** Assembly create — `well_uwi` required on POST; the rest optional. */
export interface AssemblyCreate {
  well_uwi: string;
  wellbore_uwi?: string | null;
  assembly_name?: string | null;
  assembly_no?: number | null;
  string_type_entry_key?: string | null;
  phase_entry_key?: string | null;
  is_casing_liner?: boolean | null;
  md_top?: number | null;
  md_base?: number | null;
  hole_size?: number | null;
  hole_size_uom?: string | null;
  length_total?: number | null;
  date_report?: string | null;
  change_reason?: string | null;
}
/** Assembly update — every field optional (partial edit); `well_uwi` not resent. */
export type AssemblyUpdate = Omit<AssemblyCreate, "well_uwi"> & { well_uwi?: string };

export interface CasingCreate {
  well_uwi: string;
  assembly_id?: string | null;
  report_no?: number | null;
  date_report?: string | null;
  change_reason?: string | null;
}
export type CasingUpdate = Omit<CasingCreate, "well_uwi"> & { well_uwi?: string };

export interface PipeTallyCreate {
  well_uwi: string;
  pipe_run_ref?: string | null;
  run_number?: number | null;
  joint_number?: number | null;
  sequence_no?: number | null;
  length?: number | null;
  cum_length?: number | null;
  is_out?: boolean | null;
  change_reason?: string | null;
}
export type PipeTallyUpdate = Omit<PipeTallyCreate, "well_uwi"> & { well_uwi?: string };

/**
 * Full-job tubulars roll-up (GET /wells/{well_uwi}/full-job/tubulars) —
 * aggregated across the whole well. Reuses the per-row Out shapes.
 */
export interface FullJobTubularsResponse {
  well_uwi: string;
  unit_profile_key: string | null;
  assemblies: AssemblyOut[];
  casing: CasingOut[];
  pipe_tally: PipeTallyOut[];
}

// Refetch the event's Tubulars list after any write.
const tubularsListTag = (eventId: string) =>
  [{ type: "Tubulars" as const, id: `EVENT-${eventId}` }];

export const tubularsApi = pApi.injectEndpoints({
  endpoints: (b) => ({
    // ── Assemblies ──────────────────────────────────────────────────────────
    createAssembly: b.mutation<AssemblyOut, { eventId: string; body: AssemblyCreate }>({
      query: ({ eventId, body }) => ({
        url: `/events/${eventId}/tubulars/assemblies`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { eventId }) => tubularsListTag(eventId),
    }),
    updateAssembly: b.mutation<
      AssemblyOut,
      { eventId: string; entityId: string; body: AssemblyUpdate }
    >({
      query: ({ eventId, entityId, body }) => ({
        url: `/events/${eventId}/tubulars/assemblies/${entityId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { eventId }) => tubularsListTag(eventId),
    }),

    // ── Casing ──────────────────────────────────────────────────────────────
    createCasing: b.mutation<CasingOut, { eventId: string; body: CasingCreate }>({
      query: ({ eventId, body }) => ({
        url: `/events/${eventId}/tubulars/casing`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { eventId }) => tubularsListTag(eventId),
    }),
    updateCasing: b.mutation<
      CasingOut,
      { eventId: string; entityId: string; body: CasingUpdate }
    >({
      query: ({ eventId, entityId, body }) => ({
        url: `/events/${eventId}/tubulars/casing/${entityId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { eventId }) => tubularsListTag(eventId),
    }),

    // ── Pipe tally ──────────────────────────────────────────────────────────
    createPipeTally: b.mutation<PipeTallyOut, { eventId: string; body: PipeTallyCreate }>({
      query: ({ eventId, body }) => ({
        url: `/events/${eventId}/tubulars/pipe-tally`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { eventId }) => tubularsListTag(eventId),
    }),
    updatePipeTally: b.mutation<
      PipeTallyOut,
      { eventId: string; entityId: string; body: PipeTallyUpdate }
    >({
      query: ({ eventId, entityId, body }) => ({
        url: `/events/${eventId}/tubulars/pipe-tally/${entityId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { eventId }) => tubularsListTag(eventId),
    }),

    // ── Full-job roll-up (whole well) ───────────────────────────────────────
    getFullJobTubulars: b.query<FullJobTubularsResponse, string>({
      query: (wellUwi) => `/wells/${wellUwi}/full-job/tubulars`,
      providesTags: (_r, _e, wellUwi) => [{ type: "Tubulars", id: `FULLJOB-${wellUwi}` }],
    }),
  }),
});

export const {
  useCreateAssemblyMutation,
  useUpdateAssemblyMutation,
  useCreateCasingMutation,
  useUpdateCasingMutation,
  useCreatePipeTallyMutation,
  useUpdatePipeTallyMutation,
  useGetFullJobTubularsQuery,
} = tubularsApi;
